import {
  DynamicDocumentItem,
  DynamicDocumentItemData,
  DynamicDocumentRequired,
  InstallationModuleData
} from "@/types";

/**
 * Evaluates if an item is required based on its required property and the current document data.
 * If the required param is simply a boolean, it will be returned as is.
 * If the required param is an object, it will be evaluated based on the field value and the value condition.
 * If the required param is an array of objects, every condition must match (AND).
 *
 * @param item The document item to evaluate
 * @param documentData The current document data
 * @param moduleData Optional module data that may contain the actual field values
 * @returns True if the item is required and meets the condition(s), false otherwise
 */
export function isItemRequired(
  item: DynamicDocumentItem,
  documentData: DynamicDocumentItemData[],
  moduleData?: InstallationModuleData | null
): boolean {
  if (item.required === undefined) {
    return false;
  }

  if (typeof item.required === "boolean") {
    return item.required;
  }

  const conditions = Array.isArray(item.required) ? item.required : [item.required];

  return conditions.every(condition => matchesCondition(condition, documentData, moduleData));
}

function matchesCondition(
  requiredObj: DynamicDocumentRequired,
  documentData: DynamicDocumentItemData[],
  moduleData?: InstallationModuleData | null
): boolean {
  const fieldData = documentData.find(data => data.field === requiredObj.field);

  if (!fieldData && !moduleData) {
    return false;
  }

  const fieldValue =
    moduleData && moduleData[requiredObj.field] !== undefined
      ? moduleData[requiredObj.field]
      : fieldData?.value;
  const requiredValue = requiredObj.value;

  if (fieldValue === undefined) {
    return false;
  }

  if (!requiredObj.valueCondition) {
    return fieldValue === requiredValue;
  } else {
    switch (requiredObj.valueCondition) {
      case "gt":
        return Number(fieldValue) > Number(requiredValue);
      case "gte":
        return Number(fieldValue) >= Number(requiredValue);
      case "lt":
        return Number(fieldValue) < Number(requiredValue);
      case "lte":
        return Number(fieldValue) <= Number(requiredValue);
      default:
        return false;
    }
  }
}
